<template>
  <v-card class="ncrm-mailjet-panel mt-4" variant="flat" border>
    <div class="ncrm-mailjet-panel__header">
      <div class="ncrm-mailjet-panel__title">
        <strong>{{ account.name }}</strong>
        <span v-if="account.mailjet_account_email" class="ml-2 text-caption text-medium-emphasis">
          {{ account.mailjet_account_email }}
        </span>
      </div>
      <div class="ncrm-mailjet-panel__actions">
        <NcrmMailjetAutoSyncControl
          :account="account"
          @notify="$emit('notify', $event)"
          @account-updated="$emit('account-updated', $event)"
        />
        <v-btn
          variant="tonal"
          color="primary"
          :loading="syncingFull"
          :disabled="syncingFull || !!syncingEntity"
          @click="runFullSync"
        >
          <v-icon start size="16">mdi-sync</v-icon>
          {{ $t("ncrm.integrations.mailjet.panel.syncFull") }}
        </v-btn>
        <v-menu offset-y>
          <template #activator="{ props }">
            <v-btn variant="text" v-bind="props" :disabled="syncingFull || !!syncingEntity">
              {{ $t("ncrm.integrations.mailjet.panel.syncEntity") }}
              <v-icon end size="16">mdi-menu-down</v-icon>
            </v-btn>
          </template>
          <v-list density="comfortable">
            <v-list-item
              v-for="ent in syncableEntities"
              :key="ent"
              :disabled="syncingEntity === ent"
              @click="runEntitySync(ent)"
            >
              <v-list-item-title>
                {{ $t(`ncrm.integrations.mailjet.entities.${ent}`) }}
                <v-progress-circular v-if="syncingEntity === ent" size="14" width="2" indeterminate class="ml-2" />
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </div>
    <v-divider />
    <v-tabs v-model="activeTab" color="primary" density="comfortable">
      <v-tab value="lists">{{ $t("ncrm.integrations.mailjet.entities.lists") }}</v-tab>
      <v-tab value="contacts">{{ $t("ncrm.integrations.mailjet.entities.contacts") }}</v-tab>
      <v-tab value="campaigns">{{ $t("ncrm.integrations.mailjet.entities.campaigns") }}</v-tab>
      <v-tab value="templates">{{ $t("ncrm.integrations.mailjet.entities.templates") }}</v-tab>
      <v-tab value="privacyLists">{{ $t("ncrm.integrations.mailjet.entities.privacyLists") }}</v-tab>
      <v-tab value="events">
        {{ $t("ncrm.integrations.mailjet.entities.events") }}
        <v-icon v-if="account.webhook_status === 'active' && account.webhook_last_error" size="14" color="warning" class="ml-1">
          mdi-alert
        </v-icon>
      </v-tab>
      <v-tab value="webhooks">
        {{ $t("ncrm.integrations.mailjet.entities.webhooks") }}
        <v-icon
          v-if="account.webhook_status === 'active'"
          size="10"
          :color="account.webhook_last_error ? 'warning' : 'success'"
          class="ml-1"
        >
          mdi-circle
        </v-icon>
      </v-tab>
      <v-tab value="syncLog">{{ $t("ncrm.integrations.mailjet.entities.syncLog") }}</v-tab>
      <v-tab value="propertyMappings">{{ $t("ncrm.integrations.mailjet.entities.propertyMappings") }}</v-tab>
    </v-tabs>
    <v-divider />
    <v-card-text class="pa-3">
      <v-window v-model="activeTab" :touch="false">
        <v-window-item value="lists">
          <NcrmMailjetListsTab
            v-if="activeTab === 'lists'"
            :account-id="accountId"
            @open-members="openMembers"
            @notify="$emit('notify', $event)"
          />
        </v-window-item>
        <v-window-item value="contacts">
          <NcrmMailjetContactsTab
            v-if="activeTab === 'contacts'"
            ref="contactsTabRef"
            :account-id="accountId"
            @notify="$emit('notify', $event)"
          />
        </v-window-item>
        <v-window-item value="campaigns">
          <v-data-table-server
            :headers="campaignsHeaders"
            :items="campaigns"
            :items-length="campaignsTotal"
            :loading="campaignsLoading"
            v-model:items-per-page="campaignsPerPage"
            v-model:page="campaignsPage"
            :items-per-page-options="[25, 50, 100]"
            density="comfortable"
            class="elevation-0"
            item-key="id"
          >
            <template #item.sent_at="{ item }">{{ formatDate(item.sent_at) }}</template>
            <template #item.list_name="{ item }">
              <span v-if="item.list_name">{{ item.list_name }}</span>
              <span v-else class="text-medium-emphasis">—</span>
            </template>
            <template #no-data>
              <v-alert type="info" class="ma-2">
                {{ $t("ncrm.integrations.mailjet.panel.empty.campaigns") }}
              </v-alert>
            </template>
          </v-data-table-server>
        </v-window-item>
        <v-window-item value="templates">
          <NcrmMailjetTemplatesTab v-if="activeTab === 'templates'" :account-id="accountId" @notify="$emit('notify', $event)" />
        </v-window-item>
        <v-window-item value="privacyLists">
          <NcrmMailjetListPurposesTab v-if="activeTab === 'privacyLists'" ref="privacyListsTab" :account-id="accountId" />
        </v-window-item>
        <v-window-item value="events">
          <ncrm-mailjet-events-tab :account="account" @notify="$emit('notify', $event)" />
        </v-window-item>
        <v-window-item value="webhooks">
          <ncrm-mailjet-webhooks-tab
            :account="account"
            @notify="$emit('notify', $event)"
            @account-updated="$emit('account-updated', $event)"
          />
        </v-window-item>
        <v-window-item value="syncLog">
          <v-data-table
            :headers="syncLogHeaders"
            :items="syncLog"
            :loading="syncLogLoading"
            density="comfortable"
            class="elevation-0"
            item-key="id"
          >
            <template #item.entity="{ item }">
              {{ $t(`ncrm.integrations.mailjet.entities.${item.entity}`) }}
            </template>
            <template #item.status="{ item }">
              <v-chip size="x-small" :color="syncStatusColor(item.status)">
                {{ item.status }}
              </v-chip>
            </template>
            <template #item.started_at="{ item }">{{ formatDate(item.started_at) }}</template>
            <template #item.duration="{ item }">{{ formatDuration(item.started_at, item.finished_at) }}</template>
            <template #item.counts="{ item }">{{ item.items_processed }} / {{ item.items_failed }}</template>
            <template #item.error_message="{ item }">
              <span v-if="item.error_message" class="text-caption">{{ item.error_message }}</span>
              <span v-else class="text-medium-emphasis">—</span>
            </template>
          </v-data-table>
        </v-window-item>
        <v-window-item value="propertyMappings">
          <NcrmMailjetPropertyMappingsTab
            v-if="activeTab === 'propertyMappings'"
            :account-id="accountId"
            @notify="$emit('notify', $event)"
          />
        </v-window-item>
      </v-window>
    </v-card-text>
    <v-dialog v-model="membersDialog.show" max-width="800" scrollable>
      <v-card v-if="membersDialog.list">
        <v-card-title class="d-flex align-center flex-wrap ga-3">
          <span>
            {{ $t("ncrm.integrations.mailjet.panel.membersOf", { name: membersDialog.list.name }) }}
          </span>
          <v-spacer />
          <v-switch
            v-model="membersIncludeInactive"
            :label="$t('ncrm.integrations.mailjet.panel.showUnsubscribed')"
            density="compact"
            color="primary"
            hide-details
            inset
          />
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-0">
          <v-data-table-server
            :headers="memberHeaders"
            :items="members"
            :items-length="membersTotal"
            :loading="membersLoading"
            v-model:items-per-page="membersPerPage"
            v-model:page="membersPage"
            :items-per-page-options="[25, 50, 100]"
            density="comfortable"
            class="elevation-0"
            item-key="id"
          >
            <template #item.contact_email="{ item }">
              <strong>{{ item.contact_email }}</strong>
            </template>
            <template #item.is_unsubscribed="{ item }">
              <v-chip
                size="x-small"
                :color="item.is_unsubscribed ? 'error' : 'success'"
              >
                {{
                  item.is_unsubscribed
                    ? $t("ncrm.integrations.mailjet.panel.unsubscribed")
                    : $t("ncrm.integrations.mailjet.panel.subscribed")
                }}
              </v-chip>
            </template>
          </v-data-table-server>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="membersDialog.show = false">
            {{ $t("ncrm.integrations.mailjet.dialog.actions.cancel") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>
<script>
import axios from "axios";
import NcrmMailjetEventsTab from "./NcrmMailjetEventsTab.vue";
import NcrmMailjetListsTab from "./NcrmMailjetListsTab.vue";
import NcrmMailjetTemplatesTab from "./NcrmMailjetTemplatesTab.vue";
import NcrmMailjetListPurposesTab from "./NcrmMailjetListPurposesTab.vue";
import NcrmMailjetWebhooksTab from "./NcrmMailjetWebhooksTab.vue";
import NcrmMailjetPropertyMappingsTab from "./NcrmMailjetPropertyMappingsTab.vue";
import NcrmMailjetContactsTab from "./NcrmMailjetContactsTab.vue";
import NcrmMailjetAutoSyncControl from "./NcrmMailjetAutoSyncControl.vue";
import { formatDisplayDateTime } from "@/Utile/displayFormat.mjs";
const ENTITY_KEYS = ["lists", "templates", "contacts", "membership", "campaigns"];
export default {
  name: "NcrmMailjetDataPanel",
  components: {
    NcrmMailjetEventsTab,
    NcrmMailjetListsTab,
    NcrmMailjetTemplatesTab,
    NcrmMailjetListPurposesTab,
    NcrmMailjetWebhooksTab,
    NcrmMailjetPropertyMappingsTab,
    NcrmMailjetContactsTab,
    NcrmMailjetAutoSyncControl,
  },
  props: {
    account: { type: Object, required: true },
  },
  emits: ["sync-completed", "notify", "account-updated"],
  data() {
    return {
      activeTab: this.$route?.query?.mailjetPanel === "privacyLists" ? "privacyLists" : "lists", // returnTo context
      syncingFull: false,
      syncingEntity: null,
      campaigns: [],
      campaignsTotal: 0,
      campaignsLoading: false,
      campaignsPage: 1,
      campaignsPerPage: 50,
      syncLog: [],
      syncLogLoading: false,
      membersDialog: { show: false, list: null },
      members: [],
      membersTotal: 0,
      membersLoading: false,
      membersPage: 1,
      membersPerPage: 50,
      membersIncludeInactive: true,
    };
  },
  computed: {
    syncableEntities() {
      return ENTITY_KEYS;
    },
    campaignsHeaders() {
      return [
        { title: this.$t("ncrm.integrations.mailjet.panel.headers.subject"), key: "subject" },
        { title: this.$t("ncrm.integrations.mailjet.panel.headers.list"), key: "list_name" },
        { title: this.$t("ncrm.integrations.mailjet.panel.headers.recipients"), key: "recipients_count", align: "end" },
        { title: this.$t("ncrm.integrations.mailjet.panel.headers.opens"), key: "opened_count", align: "end" },
        { title: this.$t("ncrm.integrations.mailjet.panel.headers.clicks"), key: "clicked_count", align: "end" },
        { title: this.$t("ncrm.integrations.mailjet.panel.headers.sentAt"), key: "sent_at" },
      ];
    },
    syncLogHeaders() {
      return [
        { title: this.$t("ncrm.integrations.mailjet.panel.headers.entity"), key: "entity" },
        { title: this.$t("ncrm.integrations.mailjet.panel.headers.status"), key: "status" },
        { title: this.$t("ncrm.integrations.mailjet.panel.headers.itemsProcessedFailed"), key: "counts", sortable: false },
        { title: this.$t("ncrm.integrations.mailjet.panel.headers.startedAt"), key: "started_at" },
        { title: this.$t("ncrm.integrations.mailjet.panel.headers.duration"), key: "duration", sortable: false },
        { title: this.$t("ncrm.integrations.mailjet.panel.headers.errorMessage"), key: "error_message" },
      ];
    },
    memberHeaders() {
      return [
        { title: this.$t("ncrm.integrations.mailjet.panel.headers.email"), key: "contact_email" },
        { title: this.$t("ncrm.integrations.mailjet.panel.headers.fullName"), key: "contact_name" },
        { title: this.$t("ncrm.integrations.mailjet.panel.headers.subscription"), key: "is_unsubscribed", sortable: false },
      ];
    },
    accountId() {
      return this.account?.id || null;
    },
  },
  watch: {
    account: {
      handler() {
        this.refreshActiveTab();
      },
      immediate: true,
    },
    activeTab(tab) {
      this.refreshActiveTab();
      this.$router?.replace({ query: { ...this.$route.query, mailjetPanel: tab } }).catch(() => {});
    },
    campaignsPage() { this.fetchCampaigns(); },
    campaignsPerPage() { this.campaignsPage = 1; this.fetchCampaigns(); },
    membersPage() { this.fetchMembers(); },
    membersPerPage() { this.membersPage = 1; this.fetchMembers(); },
    membersIncludeInactive() { this.membersPage = 1; this.fetchMembers(); },
  },
  methods: {
    formatDate(value) {
      if (!value) return "—";
      try { return formatDisplayDateTime(value); } catch { return String(value); }
    },
    formatDuration(start, end) {
      if (!start || !end) return "—";
      try {
        const ms = new Date(end).getTime() - new Date(start).getTime();
        if (!Number.isFinite(ms) || ms < 0) return "—";
        if (ms < 1000) return `${ms}ms`;
        if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
        return `${Math.round(ms / 60_000)}min`;
      } catch { return "—"; }
    },
    syncStatusColor(status) {
      if (status === "success") return "success";
      if (status === "partial") return "warning";
      if (status === "failed") return "error";
      if (status === "running") return "info";
      return "neutral";
    },
    extractError(error, fallbackKey) {
      return error?.response?.data?.error || error?.message || this.$t(fallbackKey);
    },
    refreshActiveTab() {
      if (!this.accountId) return;
      switch (this.activeTab) {
        case "lists": break;
        // contacts is owned by NcrmMailjetContactsTab now — its own watcher
        // on accountId triggers the initial fetch.
        case "contacts": break;
        case "campaigns": this.campaignsPage = 1; this.fetchCampaigns(); break;
        case "templates": break;
        case "syncLog": this.fetchSyncLog(); break;
        default: break;
      }
    },
    async fetchCampaigns() {
      if (!this.accountId) return;
      this.campaignsLoading = true;
      try {
        const params = {
          limit: this.campaignsPerPage,
          offset: (this.campaignsPage - 1) * this.campaignsPerPage,
        };
        const { data } = await axios.get(
          `/ncrm/integrations/mailjet/accounts/${this.accountId}/campaigns`,
          { params }
        );
        this.campaigns = Array.isArray(data?.rows) ? data.rows : [];
        this.campaignsTotal = Number(data?.total || 0);
      } catch (error) {
        this.$emit("notify", {
          color: "error",
          message: this.extractError(error, "ncrm.integrations.mailjet.panel.errors.campaigns"),
        });
      } finally {
        this.campaignsLoading = false;
      }
    },
    async fetchSyncLog() {
      if (!this.accountId) return;
      this.syncLogLoading = true;
      try {
        const { data } = await axios.get(
          `/ncrm/integrations/mailjet/accounts/${this.accountId}/sync-log?limit=100`
        );
        this.syncLog = Array.isArray(data?.entries) ? data.entries : [];
      } catch (error) {
        this.$emit("notify", {
          color: "error",
          message: this.extractError(error, "ncrm.integrations.mailjet.panel.errors.syncLog"),
        });
      } finally {
        this.syncLogLoading = false;
      }
    },
    openMembers(list) {
      this.membersDialog = { show: true, list };
      this.members = [];
      this.membersTotal = 0;
      this.membersPage = 1;
      this.fetchMembers();
    },
    async fetchMembers() {
      if (!this.accountId || !this.membersDialog.list?.id) return;
      this.membersLoading = true;
      try {
        const params = {
          limit: this.membersPerPage,
          offset: (this.membersPage - 1) * this.membersPerPage,
          includeInactive: this.membersIncludeInactive ? "true" : "false",
        };
        const { data } = await axios.get(
          `/ncrm/integrations/mailjet/accounts/${this.accountId}/lists/${this.membersDialog.list.id}/members`,
          { params }
        );
        this.members = Array.isArray(data?.rows) ? data.rows : [];
        this.membersTotal = Number(data?.total || 0);
      } catch (error) {
        this.$emit("notify", {
          color: "error",
          message: this.extractError(error, "ncrm.integrations.mailjet.panel.errors.members"),
        });
      } finally {
        this.membersLoading = false;
      }
    },
    async runFullSync() {
      if (!this.accountId) return;
      this.syncingFull = true;
      try {
        const { data } = await axios.post(
          `/ncrm/integrations/mailjet/accounts/${this.accountId}/sync`,
          {}
        );
        const summary = data?.summary || {};
        const totals = ENTITY_KEYS.reduce(
          (acc, key) => {
            const r = summary[key] || {};
            acc.processed += Number(r.processed || 0);
            acc.failed += Number(r.failed || 0);
            return acc;
          },
          { processed: 0, failed: 0 }
        );
        this.$emit("notify", {
          color: data?.ok ? "success" : "warning",
          message: this.$t("ncrm.integrations.mailjet.panel.syncSummary", totals),
        });
        this.$emit("sync-completed");
        this.activeTab = "syncLog";
        this.fetchSyncLog();
      } catch (error) {
        this.$emit("notify", {
          color: "error",
          message: this.extractError(error, "ncrm.integrations.mailjet.panel.errors.syncFull"),
        });
      } finally {
        this.syncingFull = false;
      }
    },
    async runEntitySync(entity) {
      if (!this.accountId) return;
      this.syncingEntity = entity;
      try {
        const { data } = await axios.post(
          `/ncrm/integrations/mailjet/accounts/${this.accountId}/sync?entity=${entity}`,
          {}
        );
        const r = data?.result || {};
        this.$emit("notify", {
          color: r.ok ? "success" : "error",
          message: this.$t("ncrm.integrations.mailjet.panel.syncEntitySummary", {
            entity: this.$t(`ncrm.integrations.mailjet.entities.${entity}`),
            processed: Number(r.processed || 0),
            failed: Number(r.failed || 0),
          }),
        });
        this.$emit("sync-completed");
        this.refreshActiveTab();
        this.$refs.privacyListsTab?.fetchLists?.();
      } catch (error) {
        this.$emit("notify", {
          color: "error",
          message: this.extractError(error, "ncrm.integrations.mailjet.panel.errors.syncEntity"),
        });
      } finally {
        this.syncingEntity = null;
      }
    },
  },
};
</script>
<style scoped>
.ncrm-mailjet-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 12px 16px;
}
.ncrm-mailjet-panel__title strong {
  font-size: 1rem;
  color: var(--app-text-strong);
}
.ncrm-mailjet-panel__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}
@media (max-width: 720px) {
  .ncrm-mailjet-panel__actions {
    align-items: flex-start;
    width: 100%;
  }
}
</style>
